import './App.css'
import { useState, useRef, useEffect, useCallback } from 'react'

const rows = 6
const cols = 5

const keyboardLayout = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', 'Backspace'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ö', 'ä', 'JAA'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm', 'Enter']
]

const allowedChars = keyboardLayout
  .flat()
  .filter((char) => char !== 'JAA')

const timerStorageKey = (date) => `sanaseppo-timer-${date}`

const loadTimerState = (date) => {
  try {
    const raw = localStorage.getItem(timerStorageKey(date))
    return raw ? JSON.parse(raw) : null
  } catch (err) {
    return null
  }
}

const saveTimerState = (date, state) => {
  try {
    localStorage.setItem(timerStorageKey(date), JSON.stringify(state))
  } catch (err) {
    // ignore storage errors (e.g. private browsing quota)
  }
}

// Wall-clock fallback used when there's no local active-time record for this
// game yet (fresh game, or localStorage was cleared/unavailable mid-game).
// game.startedAt/completedAt are server-authoritative, so this is exact for a
// completed game and a reasonable approximation for one still in progress.
const timerBaselineSeconds = (startedAt, isComplete, completedAt) => {
  if (!startedAt) return 0
  const end = isComplete && completedAt ? completedAt : Date.now()
  return Math.max(0, (end - startedAt) / 1000)
}

const computeElapsed = (state) => {
  if (!state) return 0
  const runningDelta = state.lastStart != null ? (Date.now() - state.lastStart) / 1000 : 0
  return state.accumulated + runningDelta
}

const formatElapsed = (totalSeconds) => {
  const seconds = Math.max(0, Math.floor(totalSeconds))
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

function App() {

  const [ guess, setGuess ] = useState([])
  const [ game, setGame ] = useState()
  const [flippingRow, setFlippingRow] = useState(null)
  const [notification, setNotification] = useState({ type: '', message: '', visible: false })
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const mounted = useRef(null)
  const notificationTimeout = useRef(null)
  const previousGuessCount = useRef(0)
  const timerRef = useRef({ date: null, accumulated: 0, lastStart: null })

  const showNotification = useCallback((type, message, timeout = 4000) => {
    if (notificationTimeout.current) {
      clearTimeout(notificationTimeout.current)
    }

    mounted.current && setNotification({ type, message, visible: true })

    notificationTimeout.current = setTimeout(() => {
      mounted.current && setNotification({ type: '', message: '', visible: false })
    }, timeout)
  }, [])

  const fetchGameData = useCallback(async () => {
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/game', {
        credentials: 'include'
      })

      if (!response.ok) {
        showNotification('error', 'Pelin lataaminen epäonnistui')
        return
      }

      const game = await response.json()
      mounted.current && setGame(game)

      if (game.isComplete && !game.isWon && game.word) {
        showNotification('error', `Seppo päihitti sinut sanalla ”${game.word}”`)
      }
    } catch (err) {
      showNotification('error', 'Yhteysvirhe pelipalveluun')
    }
  }, [ showNotification ])

  const sendWord = useCallback(async (e) => {
    e?.preventDefault?.()

    if (guess.length < cols) {
      showNotification('error', 'Liian lyhyt sana')
      return
    }

    try {
      const guessResponse = await fetch(process.env.REACT_APP_API_URL + '/guess', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(guess),
        credentials: 'include'
      })

      if (!guessResponse.ok) {
        showNotification('error', 'Arvauksen lähetys epäonnistui')
        return
      }

      fetchGameData()
      const notificationJson = await guessResponse.json()

      if (notificationJson.type !== 'error') {
        setGuess([])
      }

      if (notificationJson.message) {
        showNotification(notificationJson.type, notificationJson.message)
      }
    } catch (err) {
      showNotification('error', 'Yhteysvirhe pelipalveluun')
    }
  }, [ guess, fetchGameData, showNotification ])

  const handleCharInput = useCallback((e) => {

    if (game?.isComplete) return

    const rawChar = e.key ?? e.target.value
    const char = rawChar?.length === 1 ? rawChar.toLowerCase() : rawChar

    if (char === 'Enter') {
      sendWord(e)
      return
    }

    if (!allowedChars.includes(char)) return

    if (char === 'Backspace' && guess.length > 0) {
      setGuess(prev => prev.slice(0, -1))
    } else if (char !== 'Backspace' && guess.length < cols){
      setGuess(prev => prev.concat(char))
    }
  }, [ sendWord, guess, game?.isComplete ])


  useEffect(() => {
    fetchGameData()
  }, [fetchGameData])

  useEffect(() => {
    mounted.current = true
    document.addEventListener('keydown', handleCharInput)

    return () => {
      mounted.current = false
      if (notificationTimeout.current) {
        clearTimeout(notificationTimeout.current)
      }
      document.removeEventListener('keydown', handleCharInput)
    }
  }, [ handleCharInput ] )

  useEffect(() => {
    const currentGuessCount = game?.guesses?.length ?? 0

    if (currentGuessCount > previousGuessCount.current) {
      setFlippingRow(currentGuessCount - 1)
    }

    previousGuessCount.current = currentGuessCount
  }, [game?.guesses?.length])

  // Start / resume / stop the elapsed-time timer for the current game's day.
  useEffect(() => {
    if (!game?.date) return

    let state = loadTimerState(game.date)

    if (!state) {
      state = { accumulated: timerBaselineSeconds(game.startedAt, game.isComplete, game.completedAt), lastStart: game.isComplete ? null : Date.now() }
    } else if (game.isComplete && state.lastStart != null) {
      state = { accumulated: computeElapsed(state), lastStart: null }
    } else if (!game.isComplete && state.lastStart == null && !document.hidden) {
      state = { accumulated: state.accumulated, lastStart: Date.now() }
    }

    saveTimerState(game.date, state)
    timerRef.current = { date: game.date, ...state }
    setElapsedSeconds(computeElapsed(state))
  }, [game?.date, game?.isComplete, game?.startedAt, game?.completedAt])

  // Tick the displayed elapsed time once per second while the game is running.
  useEffect(() => {
    if (game?.isComplete) return

    const interval = setInterval(() => {
      setElapsedSeconds(computeElapsed(timerRef.current))
    }, 1000)

    return () => clearInterval(interval)
  }, [game?.date, game?.isComplete])

  // Pause the timer when the tab loses focus, resume it when it regains focus.
  useEffect(() => {
    const handleVisibilityChange = () => {
      const { date, accumulated, lastStart } = timerRef.current
      if (!date || game?.isComplete) return

      const nextState = document.hidden
        ? { accumulated: computeElapsed({ accumulated, lastStart }), lastStart: null }
        : { accumulated, lastStart: Date.now() }

      saveTimerState(date, nextState)
      timerRef.current = { date, ...nextState }
      setElapsedSeconds(computeElapsed(nextState))
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [game?.isComplete])

  const getCell = (row, col) => {
    if (!game) return null

    // check active quess
    if (!game.guesses[row] && game.guesses.length === row && guess.length <= cols && typeof guess[col] == 'string') {
      return <span className="letter" data-state="guess">{ guess[col] }</span>
    }

    // check previous guesses
    if (!game || !game.guesses[row] || !game.guesses[row][col] ) return null

    const [ char, status ] = game.guesses[row][col]
    const shouldFlip = row === flippingRow

    return (
      <span
        className={`letter${shouldFlip ? ' letter--flip' : ''}`}
        style={shouldFlip ? { '--flip-delay': `${col * 120}ms` } : undefined}
        data-state={status}
      >
        { char }
      </span>
    )
  }

  const copyToClipboard = useCallback(async (str) => {
    try {
      await navigator.clipboard.writeText(str)
      showNotification('success', 'Tulos kopioitu leikepöydälle')
    } catch (err) {
      showNotification('error', 'Tuloksen kopioiminen epäonnistui')
    }
  }, [ showNotification ])

  const handleShare = useCallback(async () => {
    if (!game || !game.guesses) {
      showNotification('error', 'Peliä ei ole vielä ladattu')
      return
    }

    const getEmoji = (status) => {
      switch (status) {
        case 'correct': return '🟩'
        case 'found': return '🟨'
        case 'missed': return '⬛️'
        default: return ''
      }
    }

    const shareId = (() => {
      if (!game.date) return '#????'
      const timestamp = parseInt(game.date.replace(/-/g, ''), 10)
      return '#' + (timestamp & 0xFFFFFFFF).toString(32).toUpperCase()
    })()

    const guessCount = (() => {
      if (game.isComplete && game.isWon) return `${game.guesses.length}/${rows}`
      if (game.isComplete && !game.isWon) return `x/${rows}`
      return `*/${rows}`
    })()

    const initStr = `SS ${shareId} ${guessCount}\n`

    // share results to clipboard
    const shareStr = game.guesses.reduce((acc, row) => {
      acc += row.map((char) => getEmoji(char[1])).join('') + '\n'
      return acc
    }, initStr) + ` 🔥 ${ game.streak || '0' } 💣 ${ game.bruteForceCount || '0' } 🕒 ${ formatElapsed(elapsedSeconds) }`


    copyToClipboard(shareStr)
  }, [ game, copyToClipboard, showNotification, elapsedSeconds ])

  const renderStreak = () => {
    return `🔥 ${ game?.streak || '0' }`
  }

  const getBruteForceLevel = () => {
    if (!game?.bruteForceLimit) return 'normal'
    const remaining = game.bruteForceLimit - (game.bruteForceCount || 0)
    if (remaining <= 3) return 'danger'
    if (remaining <= 6) return 'warning'
    return 'normal'
  }

  const renderBruteForceCounter = () => {
    return `${ game?.bruteForceCount || 0 }/${ game?.bruteForceLimit || 0 } 💣`
  }

  const isSelected = (char) => {
    return guess.includes(char)
  }

  const getStatus = (char) => {
    if (!game || !game.guesses || game.guesses.length === 0) return

    const chars = game.guesses.flat()

    // char may have multiple matches, but we only need to check the most valuable one
    if (chars.some(([c, status]) => (char === c && status === 'correct'))) return 'correct'
    if (chars.some(([c, status]) => (char === c && status === 'found'))) return 'found'
    if (chars.some(([c, status]) => (char === c && status === 'missed'))) return 'missed'
  }

  return <>
    <div className="notification" data-type={notification.type} data-visible={notification.visible}>
      { notification.message }
    </div>
    <header className="brand">
      <h1>SanaSeppo</h1>
      <div className="brand__info">
        <span className="brand__streak">{ renderStreak() }</span>
        <span className="brand__timer">🕒 { formatElapsed(elapsedSeconds) }</span>
        <span className="brand__brute-force" data-level={getBruteForceLevel()}>{ renderBruteForceCounter() }</span>
      </div>
    </header>
    <main className="grid">
      { Array(rows).fill().map((_, i) =>
        <div key={i} className="grid__row">
          { Array(cols).fill().map((_, j) =>
            <div key={j} className="grid__cell">
              { getCell(i, j) }
            </div>
          )}
        </div>
      )}
    </main>
    <div className="keyboard">
      { keyboardLayout.flat().map((char) => {
        if (char === 'JAA') {
          return (
            <button type="button" onClick={handleShare} className="share" data-hilight={game?.isWon} key="share">
              JAA
            </button>
          )
        }

        const charLabel = char === 'Backspace' ? '<' : char === 'Enter' ? 'ENT' : char.toUpperCase()
        const className = char === 'Backspace' ? 'del' : char === 'Enter' ? 'enter' : undefined

        return (
          <button
            type="button"
            value={char}
            onClick={handleCharInput}
            data-selected={isSelected(char)}
            data-state={getStatus(char)}
            className={className}
            key={char}
          >
            {charLabel}
          </button>
        )
      })}
    </div>
  </>
}

export default App
